import { useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ProductItemModel } from '../../../data-layer/posts/types';
import { removeProductFromCatalog } from '../../../data-layer/products/actions';
import { useDispatch } from '../../../data-layer/useDispatch';
import { CatalogHeader } from '../../../ui/business/CatalogHeader';
import { CONTENT_HEIGHT, ProductCard } from '../../../ui/business/ProductCard';
import { DashboardPage } from '../../../ui/dashboard/DashboardPage';
import { IconNewTab } from '../../../ui/icon/IconNewTab';
import { Button } from '../../../ui/input/Button';
import { Box } from '../../../ui/layout/Box';
import { Flex } from '../../../ui/layout/Flex';
import { Typography } from '../../../ui/typography/Typography';
import { canEditBusiness, isBloggerBusiness } from '../../../utils/accessService';
import { analyticsService, EventSource } from '../../../utils/analyticsService';
import { useIsMobile } from '../../../utils/getIsMobile';
import {
    ymTrackProductCatalogClickout,
    ymTrackProductInCatalogClickout,
} from '../../../utils/metrika';
import { useTranslation } from '../../../utils/useTranslation';
import { useSearchAppMetricsAnalytics } from '../../hooks/useSearchAppMetricsAnalytics';
import { VirtualGrid } from '../../post/VirtualGrid';
import { useCurrentUser } from '../../users/useCurrentUser';
import { useCatalog } from '../useCatalog';
import { useProductsCollection } from '../useProductsCollection';
import { useSearchAppAnalytics } from './hooks/useSearchAppAnalytics';

const BUSINESS_CATALOG_NAMESPACE_PREFIX = 'business_catalog_';
const PER_PAGE = 24;
const COLUMNS = 3;
const MOBILE_COLUMNS = 2;

export const BusinessCatalog = () => {
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const t = useTranslation();
    const currentUser = useCurrentUser();
    const { trackProductClick, trackButtonClick } = useSearchAppMetricsAnalytics({
        source: 'business_catalog_page',
    });
    const [query] = useSearchParams();
    const isFromSearchApp = query.get('morda_platform') === 'search_app';
    const params = useParams();
    const catalogId = params.catalogId!;

    const { catalog, isLoading: catalogLoading } = useCatalog(catalogId);
    const namespace = `${BUSINESS_CATALOG_NAMESPACE_PREFIX}${catalogId}`;
    const { products, isInitialized, isEndReached, onLoadNext } = useProductsCollection({
        namespace,
        filters: { catalogId, amount: PER_PAGE },
    });
    const canRemoveProduct = canEditBusiness(catalog?.business?.id, currentUser);
    const showSiteButton = Boolean(
        catalog?.business?.siteUrl && !isBloggerBusiness(catalog.business?.type),
    );

    useSearchAppAnalytics({
        catalog,
        products,
        showSiteButton,
    });

    const onLoadNextProducts = useCallback(() => {
        if (!isEndReached) {
            onLoadNext({
                catalogId,
                amount: PER_PAGE,
            });
        }
    }, [isEndReached, catalogId, onLoadNext]);

    const onProductClick = useCallback(
        (product: ProductItemModel) => {
            analyticsService.trackClickOut({
                clickoutUrl: product.url,
                eventSource: EventSource.PRODUCTS,
                productId: product.id,
                businessId: catalog?.business?.id,
                target: 'product_in_catalog',
            });
            ymTrackProductInCatalogClickout({
                page: 'Каталог',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            isFromSearchApp && trackProductClick(product.id, product.url);
        },
        [catalog, currentUser, trackProductClick, isFromSearchApp],
    );

    const onCatalogClickOut = useCallback(() => {
        analyticsService.trackClickOut({
            clickoutUrl: catalog?.business?.siteUrl,
            eventSource: EventSource.PRODUCTS,
            businessId: catalog?.business?.id,
            target: 'product_catalog',
        });
        ymTrackProductCatalogClickout({
            page: 'Каталог',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
        isFromSearchApp &&
            catalog?.business?.siteUrl &&
            trackButtonClick('GoToSite', catalog.business.siteUrl);
    }, [catalog, currentUser, isFromSearchApp, trackButtonClick]);

    const onProductRemove = useCallback(
        (productId: string) => dispatch(removeProductFromCatalog(namespace, productId, catalogId)),
        [catalogId, namespace, dispatch],
    );

    return (
        <DashboardPage hideHeader={true}>
            <Flex width="100%" backgroundColor="var(--background-primary)">
                {catalog ? <CatalogHeader catalog={catalog} /> : null}
                {isInitialized && !catalogLoading && !products.length ? (
                    <Flex p={4} justifyContent="center">
                        <Typography variant="title2" weight="bold" pb={3}>
                            {t('В каталоге пока ничего нет, но вы можете найти интересное в ленте')}
                        </Typography>
                        <Button size="l" as={Link} to="/">
                            {t('Перейти в ленту')}
                        </Button>
                    </Flex>
                ) : (
                    <Flex width="100%" pl={2} pr={1}>
                        <VirtualGrid
                            items={products}
                            onLoadNext={onLoadNextProducts}
                            columns={isMobile ? MOBILE_COLUMNS : COLUMNS}
                            heightOffset={CONTENT_HEIGHT}
                            renderItem={(product) => (
                                <ProductCard
                                    product={product}
                                    canRemoveProduct={canRemoveProduct}
                                    onProductClick={() => onProductClick(product)}
                                    onProductRemove={onProductRemove}
                                />
                            )}
                        />
                    </Flex>
                )}
                {showSiteButton ? (
                    <Box px={2} pt={1} pb={2} width="100%">
                        <Button
                            as="a"
                            size="l"
                            variant="filled"
                            color="secondary"
                            textDecoration="none"
                            target="_blank"
                            href={catalog?.business?.siteUrl}
                            width="100%"
                            spacing={0.5}
                            onClick={onCatalogClickOut}
                        >
                            {catalog?.business?.shortSite}
                            <IconNewTab fontSize={14} color="inherit" />
                        </Button>
                    </Box>
                ) : null}
            </Flex>
        </DashboardPage>
    );
};
